/**
 * Home Page
 *
 * Account settings dashboard landing page with navigation cards
 * linking to all account settings sections and basic account info.
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Badge } from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useCookieSession } from '@bloomcode/auth';
import { useGetMeQuery } from '@bloomcode/graphql';
import { Activity, Bell, Globe, Lock, Palette, Shield, ShieldCheck, User } from '@bloomcode/icons';

const navCards = [
  {
    path: '/apps',
    label: 'My Apps',
    icon: Activity,
    description: 'View and manage your installed applications',
  },
  {
    path: '/profile',
    label: 'Profile',
    icon: User,
    description: 'Manage your personal information',
  },
  {
    path: '/security',
    label: 'Security',
    icon: Shield,
    description: 'MFA, passkeys, and session management',
  },
  {
    path: '/notifications',
    label: 'Notifications',
    icon: Bell,
    description: 'Configure notification preferences',
  },
  {
    path: '/regional',
    label: 'Regional',
    icon: Globe,
    description: 'Language, timezone, and currency settings',
  },
  {
    path: '/appearance',
    label: 'Appearance',
    icon: Palette,
    description: 'Theme and display preferences',
  },
  {
    path: '/password',
    label: 'Password',
    icon: Lock,
    description: 'Change your account password',
  },
  {
    path: '/privacy',
    label: 'Privacy & Data',
    icon: ShieldCheck,
    description: 'Data privacy and export options',
  },
];

export default function HomePage() {
  const { session } = useCookieSession();
  const { data: meData, loading, error } = useGetMeQuery();

  const user = meData?.getMe?.data;
  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : session?.email?.split('@')[0] || 'User';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm">
          Failed to load account information: {error.message}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Account - Bloomcode Account</title>
      </Helmet>
      <MetaTags title="Bloomcode Account" description="Manage your Bloomcode account settings" />
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold font-heading">Welcome, {displayName}</h1>
          <p className="text-muted-foreground mt-1">
            Manage your Bloomcode account settings and preferences
          </p>
        </div>

        {/* Account Info Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Account Overview</CardTitle>
            <CardDescription>Your account details at a glance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium truncate">{session?.email || '—'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Scope Level</span>
                <Badge variant="secondary" className="w-fit">
                  {session?.scopeLevel || 'TENANT'}
                </Badge>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Account Status</span>
                <Badge variant="default" className="w-fit bg-primary/10 text-primary">
                  Active
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {navCards.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} className="group">
                <Card className="h-full transition-all duration-200 hover:border-primary/50 hover:shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="rounded-lg bg-primary/10 p-2.5 group-hover:bg-primary/15 transition-colors">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {item.label}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
